import { useEffect, useRef, useState } from "react"
import { BsPersonAdd } from "react-icons/bs"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import BlurComponentWrapper from "@fuse/sharedComponents/blurWrapper/BlurComponentWrapper"
import Footer from "@fuse/sharedComponents/footer"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import LoaderComponent from "@fuse/sharedComponents/loaderComponent"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import { buddiesInfoProps, EBUDDIES_STATUS } from "@letshang/constants"
import { get, isEmpty, uniqueId } from "lodash-es"
import { useRecoilState } from "recoil"

import {
  suggestedBuddiesAtom,
  updateSuggestedBuddies,
} from "@fuse/recoil/buddies"
import { userState } from "@fuse/recoil/signup"

import { addBuddiesAPI, changePersonStatusAPI } from "@api/firebaseWrappedApis"

import { BUDDY_STATUS } from "@fuse/utils/common"
import { appStoreLink, playStoreLink } from "@fuse/utils/DeviceDection"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

import BuddyInfoModal, {
  ProfileModalHandle,
} from "@fuse/pages/people/components/buddyInfoModal"
import useBuddiesData from "@fuse/pages/people/controller/useBuddiesData"

import EyesGif from "@fuse/assets/gif/gif-eyes.gif"
import AvatarStackPlaceholder from "@fuse/assets/images/People/img-avatar-stack-placeholder.svg"
import GooglePlayGetInOn from "@fuse/assets/images/People/img-get-it-on.svg"
import GooglePlayDownload from "@fuse/assets/images/People/img-google-play-icon.svg"
import GooglePlay from "@fuse/assets/images/People/img-google-play.svg"
import IconCheck from "@fuse/assets/images/People/img-icon-check.svg"
import IconConnection from "@fuse/assets/images/People/img-icon-connection.svg"
import IconCross from "@fuse/assets/images/People/img-icon-cross.svg"
import IconFillConnection from "@fuse/assets/images/People/img-icon-fill-connection.svg"
import IconFillRequest from "@fuse/assets/images/People/img-icon-fill-request.svg"
import IconSuggestions from "@fuse/assets/images/People/img-icon-fill-suggestions.svg"
import IconRequest from "@fuse/assets/images/People/img-icon-request.svg"
import IconFillSuggestions from "@fuse/assets/images/People/img-icon-suggestions.svg"
import AppStoreDownload from "@fuse/assets/images/People/img-logo.svg"
import AppStoreDownloadText from "@fuse/assets/images/People/img-texto.png"

const bgImage = "/assets/alpha-event-not-found.svg"
export const randomImageUri = `https://picsum.photos/1018/536?random=${uniqueId()}`

enum EmptyStateKey {
  ConnectionEmptyState = "connectionEmptyState",
  SuggestionsEmptyState = "suggestionsEmptyStateData",
}

const emptyStateData = {
  [EmptyStateKey.ConnectionEmptyState]: {
    title: "No connection requests",
    description:
      "It looks like you don’t have any connection requests right now. When someone sends you a request, you’ll see it here!",
  },
  [EmptyStateKey.SuggestionsEmptyState]: {
    title: "No suggestions yet",
    description: "It looks like you don’t have any suggestion right now. ",
  },
}

const suggestions = [
  {
    id: 1,
    name: "Ava Martinez",
    location: "Upper West Side",
    photoUrl: "https://randomuser.me/api/portraits/women/44.jpg",
  },
  {
    id: 2,
    name: "Emma Johnson",
    location: "Upper West Side",
    photoUrl: "https://randomuser.me/api/portraits/women/45.jpg",
  },
  {
    id: 3,
    name: "Liam Brown",
    location: "Brooklyn",
    photoUrl: "https://randomuser.me/api/portraits/men/46.jpg",
  },
  {
    id: 4,
    name: "Olivia Wilson",
    location: "Queens",
    photoUrl: "https://randomuser.me/api/portraits/women/47.jpg",
  },
  {
    id: 5,
    name: "Noah Smith",
    location: "Manhattan",
    photoUrl: "https://randomuser.me/api/portraits/men/48.jpg",
  },
]

const connectionRequests = [
  {
    id: 1,
    name: "Ava Martinez",
    location: "Upper West Side",
    image: "https://randomuser.me/api/portraits/women/44.jpg",
  },
  {
    id: 2,
    name: "Emma Johnson",
    location: "Upper West Side",
    image: "https://randomuser.me/api/portraits/women/45.jpg",
  },
  {
    id: 3,
    name: "Liam Brown",
    location: "Brooklyn",
    image: "https://randomuser.me/api/portraits/men/46.jpg",
  },
  {
    id: 4,
    name: "Olivia Wilson",
    location: "Queens",
    image: "https://randomuser.me/api/portraits/women/47.jpg",
  },
  {
    id: 5,
    name: "Noah Smith",
    location: "Manhattan",
    image: "https://randomuser.me/api/portraits/men/48.jpg",
  },
]

const PeoplePage = () => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0)

  const [uidsBeingAddedToBuddies, setUidsBeingAddedToBuddies] = useState<
    string[]
  >([])
  const [loadingState, setLoadingState] = useState<{
    [buddyId: string]: { accept: boolean; decline: boolean }
  }>({})
  const [user, setUser] = useRecoilState(userState)
  const [isBuddyModalVisible, setIsBuddyModalVisible] = useState(false)
  const [selectedBuddy, setSelectedBuddy] = useState<buddiesInfoProps | null>(
    null,
  )
  const loggedInUserId = get(user, "uid", "")
  const [suggestedBuddiesAtomData, setSuggestedBuddiesAtom] =
    useRecoilState(suggestedBuddiesAtom)
  const modalRef = useRef<ProfileModalHandle>(null)
  const deviceType = useDeviceTypeByWidth()
  const isMobileDevice = deviceType === TDeviceType.MOBILE

  const isLoading = get(suggestedBuddiesAtomData, "loading", false)
  const {
    pendingBuddiesSection: pendingBuddies,
    suggestedBuddiesSection: suggestedBuddies,
    realBuddiesSection: realBuddies,
  } = useBuddiesData()

  const peopleTab = [
    {
      label: "Requests",
      icon: IconRequest,
      iconFill: IconFillRequest,
    },
    {
      label: "Connections",
      icon: IconConnection,
      iconFill: IconFillConnection,
    },
    {
      label: "Suggestions",
      icon: IconSuggestions,
      iconFill: IconFillSuggestions,
    },
  ]

  const urlMap: Record<number, string> = {
    0: "/people/requests",
    1: "/people/connections",
    2: "/people/suggestions",
  }

  useEffect(() => {
    const host = window.location.origin
    const url = window.location.href

    // query params
    const queryParams = url.split("?")[1]

    let newUrl = host.split("?")[0] + urlMap[currentTabIndex]
    // add query params if they exist
    if (queryParams) {
      newUrl += "?" + queryParams
    }
    if (currentTabIndex) {
      window.history.pushState({}, "", newUrl)
    } else {
      window.history.replaceState({}, "", newUrl)
    }
  }, [currentTabIndex])

  const NoDataView = ({ viewKey }: { viewKey: EmptyStateKey }) => {
    const currentData = emptyStateData[viewKey]
    return (
      <div className="my-10 mt-20 flex flex-col items-center text-center">
        <img className="w-16" src={EyesGif} />
        <h3 className="text-lg font-medium my-4 text-white">
          {currentData?.title}
        </h3>
        <p className="text-white35 max-w-lg">{currentData?.description}</p>
      </div>
    )
  }

  const closeBuddyModal = () => setIsBuddyModalVisible(false)

  const addToBuddy = async (item: any) => {
    setUidsBeingAddedToBuddies((prev) => [...prev, item?.uid])
    addBuddiesAPI({
      uid: loggedInUserId,
      friendUid: item?.uid,
      suggestedBecause: item?.suggestedBecause,
      defaultState: EBUDDIES_STATUS.ACCEPTED,
    })
      .then(async (data) => {
        await updateSuggestedBuddies(get(item, "uid", ""))
        setUidsBeingAddedToBuddies((prev) =>
          prev.filter((it) => it !== item?.uid),
        )
      })
      .catch(() => {
        setUidsBeingAddedToBuddies((prev) =>
          prev.filter((it) => it !== item?.uid),
        )
      })
  }

  const handleRequestResponse = (id: string, accepted: boolean) => {
    const actionType = accepted ? "accept" : "decline"

    console.log(`Request ${accepted ? "accepted" : "rejected"} for ID: ${id}`)

    setLoadingState((prev) => ({
      ...prev,
      [id]: {
        accept: prev[id]?.accept ?? false,
        decline: prev[id]?.decline ?? false,
        [actionType]: true,
      },
    }))

    setSuggestedBuddiesAtom({
      loading: true,
      ...suggestedBuddiesAtomData,
    })

    changePersonStatusAPI({
      peopleToChangeStatus: [
        {
          buddyId: id,
          status: accepted ? BUDDY_STATUS.ACCEPTED : BUDDY_STATUS.DENIED,
          fofStatus: accepted ? BUDDY_STATUS.ACCEPTED : BUDDY_STATUS.DENIED,
        },
      ],
    }).finally(() => {
      setLoadingState((prev) => ({
        ...prev,
        [id]: {
          accept: false,
          decline: false,
          ...prev[id],
          [actionType]: false,
        },
      }))

      setSuggestedBuddiesAtom({
        loading: false,
        ...suggestedBuddiesAtomData,
      })
    })
  }

  const onOpenBuddyModal = (buddyInfo) => {
    modalRef.current?.openModal(buddyInfo)
  }

  useEffect(() => {
    if (!user.isLoggedIn) {
      // redirect to letshang.info
      window.location.href = "https://letshang.info"
      return
    }
  }, [])

  const requestTab = () => {
    return (
      <div className="lg:max-h-[80vh] h-full overflow-y-auto mt-10 w-full">
        <p className="text-white text-lg text-start font-medium">
          Connection requests
        </p>
        <div className="mt-4">
          {!isEmpty(pendingBuddies) ? (
            <>
              {pendingBuddies?.map((user) => {
                const isAcceptLoading =
                  loadingState[user?.buddyId]?.accept || false
                const isDeclineLoading =
                  loadingState[user?.buddyId]?.decline || false
                const isCTADisabled = isAcceptLoading || isDeclineLoading
                return (
                  <div
                    key={Math.random()}
                    className="flex items-center justify-between py-4 pr-4 rounded-lg cursor-pointer"
                    onClick={() => onOpenBuddyModal(user)}
                  >
                    <div className="flex items-center gap-4">
                      <img
                        className="w-12 h-12 rounded-full bg-gray-600"
                        src={get(user, "avatarUrl", "")}
                        alt={get(user, "name", "") || "Unknown"}
                      />
                      <div className="flex flex-col text-start">
                        <p className="font-medium text-white">
                          {get(user, "name", "") || "Unknown"}
                        </p>
                        <p className="text-white35 text-sm">
                          {get(user, "locality", "") || "No area set"}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-3">
                      <div
                        className={`h-10 w-10 rounded-full border-white/35 border flex items-center justify-center cursor-pointer ${
                          isDeclineLoading ? "pointer-events-none" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          !isCTADisabled &&
                            handleRequestResponse(user?.buddyId, false)
                        }}
                      >
                        {isDeclineLoading ? (
                          <LoaderComponent size="small" />
                        ) : (
                          <img src={IconCross} alt={"IconCross"} />
                        )}
                      </div>
                      <div
                        className={`h-10 w-10 rounded-full bg-white/10 flex items-center justify-center cursor-pointer ${isDeclineLoading ? "pointer-events-none" : ""}`}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          !isCTADisabled &&
                            handleRequestResponse(user?.buddyId, true)
                        }}
                      >
                        {isAcceptLoading ? (
                          <LoaderComponent size="small" />
                        ) : (
                          <img src={IconCheck} alt={"IconCheck"} />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          ) : (
            <NoDataView viewKey={EmptyStateKey.ConnectionEmptyState} />
          )}
        </div>

        <div className="h-[0.1px] w-full bg-white opacity-20 my-6"></div>

        <p className="text-white text-lg text-start font-medium">Suggestions</p>
        <div className="mt-4">
          {!isEmpty(suggestedBuddies) ? (
            <>
              {suggestedBuddies?.map((user) => {
                const isLoading = uidsBeingAddedToBuddies.includes(user?.uid)

                return (
                  <div
                    key={Math.random()}
                    className="flex items-center justify-between py-4 pr-4 rounded-lg cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      onOpenBuddyModal(user)
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <img
                        className="w-12 h-12 rounded-full bg-gray-600"
                        src={get(user, "photoUrl", "")}
                        alt={get(user, "name", "")}
                      />
                      <div className="flex flex-col text-start">
                        <p className="font-medium text-white">
                          {get(user, "name", "")}
                        </p>
                        <p className="text-white35 text-sm">
                          {get(user, "neighborhood", "") || "No area set"}
                        </p>
                      </div>
                    </div>
                    <button
                      className="text-white text-sm px-4 py-2 bg-white/10 flex items-center justify-center cursor-pointer gap-2"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        addToBuddy(user)
                      }}
                    >
                      {isLoading && <LoaderComponent size="small" />}
                      Connect
                    </button>
                  </div>
                )
              })}
            </>
          ) : (
            <NoDataView viewKey={EmptyStateKey.SuggestionsEmptyState} />
          )}
        </div>
      </div>
    )
  }

  const connectionTab = () => {
    return (
      <div className="lg:max-h-[80vh] h-full overflow-y-auto mt-10 w-full">
        <p className="text-white text-lg text-start font-medium">
          All connections
        </p>
        <div className="mt-4">
          {!isEmpty(realBuddies) ? (
            <>
              {realBuddies?.map((user) => (
                <div
                  key={Math.random()}
                  className="flex items-center justify-between py-4 rounded-lg cursor-pointer"
                  onClick={() => onOpenBuddyModal(user)}
                >
                  <div className="flex items-center gap-4">
                    <img
                      className="w-12 h-12 rounded-full bg-gray-600"
                      src={get(user, "avatarUrl", "")}
                      alt={get(user, "name", "") || "Unknown"}
                      loading="lazy"
                    />
                    <div className="flex flex-col text-start">
                      <p className="font-medium text-white">
                        {get(user, "name", "") || "Unknown"}
                      </p>
                      {/* <p className="text-white35 text-sm">{user.location}</p> */}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <NoDataView viewKey={EmptyStateKey.ConnectionEmptyState} />
          )}
        </div>
      </div>
    )
  }

  const suggestionsTab = () => {
    return (
      <div className="lg:max-h-[80vh] h-full overflow-y-auto mt-10 w-full">
        <p className="text-white text-lg text-start font-medium">Suggestions</p>
        <div className="mt-4">
          {!isEmpty(suggestedBuddies) ? (
            <>
              {suggestedBuddies?.map((user) => {
                const isLoading = uidsBeingAddedToBuddies.includes(user?.uid)

                return (
                  <div
                    key={Math.random()}
                    className="flex items-center justify-between py-4 pr-4 rounded-lg cursor-pointer"
                    onClick={() => onOpenBuddyModal(user)}
                  >
                    <div className="flex items-center gap-4">
                      <img
                        className="w-12 h-12 rounded-full bg-gray-600"
                        src={get(user, "photoUrl", "")}
                        alt={get(user, "name", "")}
                      />
                      <div className="flex flex-col text-start">
                        <p className="font-medium text-white">
                          {get(user, "name", "")}
                        </p>
                        <p className="text-white35 text-sm">
                          {get(user, "neighborhood", "") || "No area set"}
                        </p>
                      </div>
                    </div>
                    <button
                      className="text-white text-sm px-4 py-2 bg-white/10 flex items-center justify-center cursor-pointer gap-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        addToBuddy(user)
                      }}
                    >
                      {isLoading && <LoaderComponent size="small" />}
                      Connect
                    </button>
                  </div>
                )
              })}
            </>
          ) : (
            <NoDataView viewKey={EmptyStateKey.SuggestionsEmptyState} />
          )}
        </div>
      </div>
    )
  }

  const renderTabs = () => {
    switch (currentTabIndex) {
      case 0:
        return requestTab()

      case 1:
        return connectionTab()

      case 2:
        return suggestionsTab()

      default:
        return null
    }
  }

  return (
    <BackgroundWrapper bgImage={bgImage} className="h-full">
      <BlurWrapper
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className={`h-full flex flex-col ${isMobileDevice ? "" : "overflow-auto"} min-h-[100vh]`}
      >
        <div className="fixed inset-0 w-full h-screen overflow-hidden flex flex-col">
          <div id="upper-body" className="flex">
            <Navbar />
          </div>

          {isLoading && <LoadingSpinner />}
          <div className="lg:grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-3 gap-8 w-full md:mt-[74px] mt-[-5px] md:px-8 max-w-screen-xl 2xl:max-w-screen-2xl self-center overflow-auto flex-1">
            <div className="col-span-2 lg:max-h-[80vh] lg:pb-[18vh] overflow-hidden px-4">
              <h3 className="text-xl font-semibold text-white text-start pb-3 hidden md:flex">
                People
              </h3>
              <div className="h-[0.1px] w-full bg-white opacity-20 mb-6 hidden md:flex"></div>

              {/* mobile segment */}
              <div className="md:hidden inline-flex h-9 w-full items-baseline justify-start rounded-lg p-1 backdrop-blur-md bg-gray-600">
                {peopleTab?.map((item, index) => {
                  const isSelected = index === currentTabIndex
                  return (
                    <button
                      key={Math.random()}
                      type="button"
                      aria-disabled="false"
                      className={`md:hidden group inline-flex items-center justify-center whitespace-nowrap rounded-lg py-2 align-middle font-semibold transition-all duration-300 ease-in-out disabled:cursor-not-allowed min-w-[32px] gap-1.5 text-xs h-7 w-full px-3 ${isSelected ? "text-white backdrop-blur-md bg-white/20" : "text-white/50 bg-transparent"}`}
                      onClick={() => setCurrentTabIndex(index)}
                    >
                      <img src={isSelected ? item?.iconFill : item?.icon} />
                      {item?.label}
                    </button>
                  )
                })}
              </div>
              {/* desktop pills */}
              <div className="hidden md:flex gap-4 whitespace-nowrap overflow-x-auto ">
                {peopleTab?.map((item, index) => {
                  const isSelected = index === currentTabIndex
                  return (
                    <button
                      key={Math.random()}
                      className={`hidden md:flex px-6 py-2 bg-white/10 rounded-full text-sm items-center justify-center gap-2 flex-row ${isSelected ? "text-white" : "text-white/50 opacity-70"}`}
                      onClick={() => setCurrentTabIndex(index)}
                    >
                      <img src={isSelected ? item?.iconFill : item?.icon} />
                      {item?.label}
                    </button>
                  )
                })}
              </div>
              <div className="md:flex hidden h-full">{renderTabs()}</div>
            </div>
            <div className="backdrop-blur-md bg-gray-700 px-4 mt-20 pb-10 rounded-t-2xl absolute top-32 bottom-0 left-0 right-0 md:hidden border-t">
              {renderTabs()}
            </div>

            <BlurComponentWrapper className="hidden md:flex p-12 rounded-3xl w-full flex-col items-center justify-center max-h-[80vh] mt-4 lg:mt-0 lg:col-span-2 xl:col-span-1">
              <img
                src={AvatarStackPlaceholder}
                alt="Avatar Stack Placeholder"
                className="mb-6"
              />
              <div className="w-full max-w-xs">
                <h3 className="text-lg font-semibold text-white">
                  Stay Connected with Your
                  <br /> Friends! 🎉
                </h3>
                <p className="text-gray-400 mt-2">
                  Sync your contacts to find and connect with friends instantly.
                </p>
                <p className="text-gray-400 mt-2">
                  Don’t miss out—download the app and start hanging out now!
                </p>
              </div>
              <div className="flex space-x-4 mt-16">
                <BlurComponentWrapper
                  className="flex  px-8 py-4 items-center justify-center gap-2 cursor-pointer rounded-xl"
                  onClick={() => {}}
                >
                  <BsPersonAdd className="text-white text-2xl" />
                  <div className="text-[white] font-semibold text-base">
                    Invite a friend
                  </div>
                </BlurComponentWrapper>
              </div>
            </BlurComponentWrapper>

            <BuddyInfoModal ref={modalRef} />
          </div>

          <div className="hidden md:flex bottom-0 w-full">
            <Footer />
          </div>
        </div>
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default PeoplePage
